/**
 * CommandPalette — глобальный поиск (Cmd/Ctrl+K), как в админке flowers.
 *
 * Рендерится порталом поверх страницы. Ищет по пунктам навигации (с учётом
 * роли/прав), показывает недавние страницы при пустом запросе. Клавиатура:
 * ↑/↓ — навигация, Enter — переход, Esc — закрыть.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useT } from '@/store/i18nStore';
import { useUserRole } from '@/store/authStore';
import { usePermissionStore } from '@/store/permissionStore';
import { useCommandPalette } from '@/store/uiStore';
import { navigationItems, isNavItemVisible, type NavItem } from './Sidebar';
import { getRecentPaths } from './commandPaletteRecent';

interface Leaf {
  id: string;
  path: string;
  label: string;
  icon: NavItem['icon'];
  parentLabel?: string;
}

export function CommandPalette(): JSX.Element | null {
  const { isOpen, close } = useCommandPalette();
  const navigate = useNavigate();
  const t = useT();
  const userRole = useUserRole();
  const keys = usePermissionStore((s) => s.keys);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentPaths, setRecentPaths] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Flatten visible nav leaves (children of groups, or leaf top-level items).
  const leaves = useMemo<Leaf[]>(() => {
    const out: Leaf[] = [];
    for (const item of navigationItems) {
      if (!isNavItemVisible(item, userRole, keys)) continue;
      const itemLabel = item.labelKey ? t(item.labelKey) : item.label;
      const children = item.children?.filter((c) => isNavItemVisible(c, userRole, keys)) ?? [];
      if (children.length > 0) {
        for (const c of children) {
          out.push({
            id: c.id,
            path: c.path,
            label: c.labelKey ? t(c.labelKey) : c.label,
            icon: c.icon,
            parentLabel: itemLabel,
          });
        }
      } else {
        out.push({ id: item.id, path: item.path, label: itemLabel, icon: item.icon });
      }
    }
    return out;
  }, [userRole, keys, t]);

  const recentLeaves = useMemo<Leaf[]>(
    () =>
      recentPaths
        .map((p) => leaves.find((l) => l.path === p))
        .filter((l): l is Leaf => Boolean(l)),
    [recentPaths, leaves]
  );

  const sections = useMemo<{ title: string; items: Leaf[] }[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      if (recentLeaves.length > 0) return [{ title: 'Недавние', items: recentLeaves }];
      return [{ title: 'Навигация', items: leaves }];
    }
    const filtered = leaves.filter((l) => l.label.toLowerCase().includes(q));
    return [{ title: 'Навигация', items: filtered }];
  }, [query, recentLeaves, leaves]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // Reset + focus on open; lock body scroll.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSelectedIndex(0);
    setRecentPaths(getRecentPaths());
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Keep selected row in view.
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    listRef.current.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, selectedIndex]);

  const handleSelect = useCallback(
    (item: Leaf | undefined) => {
      if (!item) return;
      navigate(item.path);
      close();
    },
    [navigate, close]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSelect(flat[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    },
    [flat, selectedIndex, handleSelect, close]
  );

  if (!isOpen) return null;

  let runningIndex = 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-start justify-center pt-[18vh] px-4"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-[544px] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-[var(--radius-xl)] shadow-[var(--shadow-modal)] overflow-hidden animate-fade-in"
        role="dialog"
        aria-modal="true"
        aria-label="Поиск"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-border)]">
          <span className="flex-shrink-0 text-[var(--color-text-secondary)]">
            <Search className="w-5 h-5" />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Поиск по разделам…"
            className="flex-1 bg-transparent text-touch md:text-md text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSelectedIndex(0);
                inputRef.current?.focus();
              }}
              className="flex-shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
              aria-label="Очистить"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[320px] overflow-y-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {flat.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-[var(--color-text-secondary)]">
              Ничего не найдено
            </div>
          )}

          {sections.map((section) => (
            <div key={section.title}>
              <div className="px-4 pt-2 pb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                  {section.title}
                </span>
              </div>
              {section.items.map((item) => {
                const idx = runningIndex++;
                const isSelected = idx === selectedIndex;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-selected={isSelected}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                      isSelected ? 'bg-[var(--color-bg-secondary)]' : 'hover:bg-[var(--color-bg-secondary)]'
                    }`}
                  >
                    <span className="flex-shrink-0 text-[var(--color-text-secondary)]">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="text-sm text-[var(--color-text-primary)] truncate">{item.label}</span>
                    {item.parentLabel && (
                      <span className="ml-auto flex-shrink-0 text-xs text-[var(--color-text-secondary)] truncate">
                        {item.parentLabel}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-[var(--color-border)] font-mono">↑↓</kbd>
            навигация
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-[var(--color-border)] font-mono">↵</kbd>
            открыть
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded border border-[var(--color-border)] font-mono">esc</kbd>
            закрыть
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
